import type { Language } from '../schemas/simulation'
import type { SubstitutionKey } from './substitutions'

/**
 * 인증 준비도 원료 사전 (2026-11-01). 코드 검사(checks.ts)가 재료·대체재 이름에서 찾는 말이다.
 *
 * 출처
 * - 할랄: certcheck halal_rules 88행 (= 식품 지식코어 halal_ingredient_rules, 2026-11-01 읽음. 영어 키워드 88행 전부)
 *   + research/halal.md 6-1 의 별칭 보완: 돼지 접두어('돼지감자' 제외)·돈육·돈지·돈피·돈골, 식품첨가물공전 이름
 *   (스테아린산·스테아린산마그네슘·글리세린디아세틸주석산지방산에스테르), PGPR, 우유응고효소, 괄호(원산지) 속 '청주' 제외.
 *   판정 등급은 사전의 haram/mushbooh 가 아니라 규칙 id 를 따른다 (H-ING 금지 / H-SRC 증빙 / H-ALC 알코올).
 *   주정·에탄올·알코올은 금지가 아니라 H-ALC-01 '전문가 확인'이다 (MUI Fatwa 10/2018 비주류 에탄올 허용, halal.md 6-5).
 * - 해썹: 식품공전 제5.12 장류·제5.13 소스 보존료·색소 (research/haccp.md, HK-STD-01~07)
 *
 * 찾는 법 (오탐 방지, halal.md 6-1)
 * - 재료 이름을 쉼표·가운뎃점·빗금·괄호로 나눈 "원료 단위" 마다 찾는다.
 * - 한·중·일 말은 띄어쓰기를 지우고 찾는다. 두 글자 이하 말('햄'·'럼')은 원료 단위와 같거나 끝이 같을 때만 (햄버거빵은 아님),
 *   그리고 그 말 뒤에 가공 꼬리(분말·농축액·단백질·뼈·베이스 … SHORT_WORD_SUFFIX)만 붙었을 때 ('치즈분말'·'혈장단백질').
 *   세 글자 이상은 들어 있으면 찾는다. contains 를 켠 말('돼지'·'豚')은 길이와 상관없이 들어 있으면 찾는다.
 * - 영어는 단어 경계로 찾는다 (graham 은 ham 이 아님).
 * - except 의 말이 원료 단위에 있으면 그 용어로 보지 않는다 (돼지감자, 와인식초, 크럼 …). except 는 그 원료 단위에만 건다 —
 *   이름 전체에 걸면 '데리야키소스(간장, 미림, 와인식초)' 의 미림이 와인식초 때문에 빠진다.
 * - requires 가 있는 용어는 그 말이 원료 단위에 함께 있을 때만 걸린다 ('무알코올 맥주' → 전문가 확인).
 *   '맛술(무알콜)' 처럼 괄호에 수식어만 있으면 앞 원료에 붙여 '무알콜 맛술' 로 읽는다 (splitIngredientUnits).
 * - notInParens 인 말은 괄호 안(원산지·지명)에서 찾지 않는다 ('쌀(국산, 청주)').
 *
 * 언어별 범위: 한국어·영어는 할랄 규칙표 88행 전부를 담았다 (TERMS_COVERAGE full). 일본어·중국어는 금지 원료와 자주 쓰는
 * 의심 원료만 담았다 (partial) — 그 언어의 결과는 걸린 것이 없어도 ✅ 대신 '확인 필요'로 둔다 (readiness.ts).
 */

export type TermAction = 'fail' | 'improve' | 'docs' | 'review'

export interface IngredientTerm {
  /** 사전 묶음 이름 (테스트·관리자 근거용) */
  key: string
  /** 걸리면 이 규칙의 항목이 된다 */
  ruleId: string
  /** fail = 금지 원료 · improve = 같은 역할의 식물성 원료로 바꾸면 서류가 준다 · docs = 서류로 증빙 · review = 전문가 확인 */
  action: TermAction
  words: string[]
  hintKey?: SubstitutionKey
  except?: string[]
  notInParens?: boolean
  /** 길이와 상관없이 들어 있으면 찾는다 (접두어 규칙) */
  contains?: boolean
  /** 이 말 중 하나가 원료 단위에 함께 있어야 걸린다 (예: 무알코올 + 술 이름) */
  requires?: string[]
  /** 같은 규칙의 다른 설명 (CERT_VARIANT_COPY 의 '규칙id:variant') */
  variant?: string
}

export const TERMS_COVERAGE: Record<Language, 'full' | 'partial'> = {
  ko: 'full',
  en: 'full',
  ja: 'partial',
  zh: 'partial',
}

const t = (
  key: string,
  ruleId: string,
  action: TermAction,
  words: string[],
  extra: Partial<Omit<IngredientTerm, 'key' | 'ruleId' | 'action' | 'words'>> = {},
): IngredientTerm => ({ key, ruleId, action, words, ...extra })

/** 술과 술을 넣은 조미료 (H-ING-04). MUI Fatwa 10/2018: 술(khamr)을 넣은 식품은 하람 */
const ALCOHOL_WORDS = [
  '맛술',
  '미림',
  '본미림',
  '요리술',
  '술',
  '와인',
  '포도주',
  '적포도주',
  '백포도주',
  '맥주',
  '럼',
  '럼주',
  '리큐어',
  '리큐르',
  '소주',
  '막걸리',
  '약주',
  '탁주',
  '과실주',
  '매실주',
  '복분자주',
  '고량주',
  '증류주',
  '사케',
  '정종',
  '브랜디',
  '위스키',
  '보드카',
  '샤오싱주',
  'mirin',
  'sake',
  'cooking wine',
  'rice wine',
  'wine',
  'beer',
  'rum',
  'liqueur',
  'brandy',
  'whisky',
  'whiskey',
  'vodka',
  'soju',
  'makgeolli',
  'sherry',
  'cognac',
  'gin',
  'champagne',
  'bourbon',
  'tequila',
  'marsala',
  'shaoxing',
  'みりん',
  '味醂',
  '料理酒',
  '日本酒',
  '清酒',
  '酒',
  '酒粕',
  'ワイン',
  'ビール',
  'ラム酒',
  'リキュール',
  '焼酎',
  '紹興酒',
  '料酒',
  '黄酒',
  '米酒',
  '绍兴酒',
  '花雕',
  '老酒',
  '葡萄酒',
  '啤酒',
  '朗姆酒',
  '利口酒',
  '白酒',
]
/** '무알코올 ○○' 의 수식어 (alcohol-free-drink 용어가 이 말과 술 이름이 함께 있을 때만 걸린다) */
const ALCOHOL_FREE_WORDS = [
  '무알코올',
  '무알콜',
  '논알코올',
  '논알콜',
  'non-alcoholic',
  'alcohol-free',
  'ノンアルコール',
  '无醇',
]
/**
 * 술 이름이 들어 있지만 술이 아닌 것. 와인식초는 식초(H-ALC-04), 맥주효모는 미생물 원료(H-SRC-06),
 * 미림풍 조미료는 대체안(복합원료 서류), 포도주스는 주스, 쿠키 크럼은 빵가루, 루트비어·진저비어는 탄산음료,
 * 주정(넣는 에탄올)은 전문가 확인(H-ALC-01), 부르봉 바닐라는 바닐라 품종이다
 */
const ALCOHOL_EXCEPT = [
  '와인식초',
  '와인비니거',
  '포도주식초',
  '셰리식초',
  'wine vinegar',
  'sherry vinegar',
  'champagne vinegar',
  '맥주효모',
  "brewer's yeast",
  'brewers yeast',
  '미림풍',
  'みりん風',
  '주스',
  // '다크럼'(럼주)은 걸리고 '쿠키크럼'(빵가루)은 빠지게 크럼 앞말까지 적는다
  '쿠키크럼',
  '브레드크럼',
  '빵크럼',
  '케이크크럼',
  '크래커크럼',
  '크럼블',
  'crumb',
  '루트비어',
  '진저비어',
  'root beer',
  'ginger beer',
  '주정',
  'bourbon vanilla',
  'ワインビネガー',
  '葡萄酒醋',
  '啤酒酵母',
  'ビール酵母',
]

/** 할랄 원료 사전. 위에서부터 먼저 걸린 것 하나만 쓴다 (돼지 젤라틴은 젤라틴이 아니라 돼지) */
export const HALAL_TERMS: IngredientTerm[] = [
  // ── 금지 원료 (H-ING, 불합격) ──
  t('pork-prefix', 'H-ING-01', 'fail', ['돼지', '豚', '猪'], {
    contains: true,
    hintKey: 'pork',
    except: ['돼지감자', '猪苓', '豚草'],
  }),
  t(
    'pork',
    'H-ING-01',
    'fail',
    [
      '돈육',
      '돈지',
      '돈피',
      '돈골',
      '돈가스',
      '돈까스',
      '라드',
      '베이컨',
      '햄',
      '삼겹살',
      '오겹살',
      '항정살',
      '갈매기살',
      '제육',
      '족발',
      '순대',
      '살라미',
      '페퍼로니',
      '프로슈토',
      '판체타',
      '초리조',
      '차슈',
      '포크',
      'pork',
      'pig',
      'swine',
      'porcine',
      'lard',
      'bacon',
      'ham',
      'gammon',
      'prosciutto',
      'pancetta',
      'guanciale',
      'mortadella',
      'pepperoni',
      'salami',
      'chorizo',
      'char siu',
      'ラード',
      'ベーコン',
      'ハム',
      'サラミ',
      'チャーシュー',
      '培根',
      '火腿',
      '腊肉',
      '腊肠',
      '叉烧',
      '五花肉',
      '东坡肉',
    ],
    {
      hintKey: 'pork',
      // 닭·칠면조·소로 만든 햄은 돼지가 아니다 (고기 원료 H-ING-06 으로 간다)
      except: [
        '치킨햄',
        '닭햄',
        '닭가슴살햄',
        '칠면조햄',
        '소고기햄',
        'chicken ham',
        'turkey ham',
        'beef ham',
      ],
    },
  ),
  t(
    'blood',
    'H-ING-02',
    'fail',
    ['선지', '혈액', '혈장', '혈분', '소피', 'blood', 'plasma', '血'],
    { hintKey: 'blood', except: ['블러드오렌지', '블러드 오렌지', 'blood orange'] },
  ),
  t(
    'forbidden-animal',
    'H-ING-03',
    'fail',
    [
      '개고기',
      '보신탕',
      '뱀',
      '원숭이',
      '곰고기',
      '웅담',
      '개구리',
      '악어',
      '자라',
      '당나귀',
      '노새',
      'dog meat',
      'snake',
      'monkey',
      'bear meat',
      'frog',
      'crocodile',
      'alligator',
      'donkey',
      'カエル',
      'ワニ',
      'スッポン',
      '狗肉',
      '蛇肉',
      '蛙',
      '鳄鱼',
      '甲鱼',
      '驴肉',
    ],
    { except: ['snake fruit', 'monkey bread', '자라섬'] },
  ),
  // '청주' 는 지명(청주시)과 같은 글자라 원산지 괄호 안에서는 찾지 않는다 ('쌀(국산, 청주)')
  t('cheongju', 'H-ING-04', 'fail', ['청주'], {
    hintKey: 'alcohol-seasoning',
    notInParens: true,
    except: ['청주산'],
  }),
  // '무알코올 맥주'·'무알콜 맛술' 은 사전에서 빼지 않는다. MUI Fatwa 10/2018 제3부 4항: 0.5% 미만 음료도 술(khamr)에서
  // 온 것이면 할랄이 아니다. 술에서 알코올을 뺀 것인지 처음부터 술이 아닌지는 앱이 모르니 전문가 확인 (아래 술 목록보다 먼저 본다)
  t('alcohol-free-drink', 'H-ALC-01', 'review', ALCOHOL_WORDS, {
    hintKey: 'alcohol-seasoning',
    requires: ALCOHOL_FREE_WORDS,
    except: ALCOHOL_EXCEPT,
  }),
  t('alcohol-seasoning', 'H-ING-04', 'fail', ALCOHOL_WORDS, {
    hintKey: 'alcohol-seasoning',
    except: ALCOHOL_EXCEPT,
  }),
  // ── 알코올 (H-ALC, 전문가 확인) ──
  t(
    'ethanol',
    'H-ALC-01',
    'review',
    [
      '주정',
      '발효주정',
      '에탄올',
      '알코올',
      '알콜',
      'ethanol',
      'ethyl alcohol',
      'alcohol',
      '酒精',
      'エタノール',
      'アルコール',
      '乙醇',
    ],
    {
      hintKey: 'alcohol-ethanol',
      // '무알코올' 이라는 말 자체는 주정이 아니고(술 이름과 함께면 위 alcohol-free-drink), 당알코올(에리스리톨 등)은 감미료다
      except: [...ALCOHOL_FREE_WORDS, '당알코올', 'sugar alcohol', '糖アルコール', '糖醇'],
    },
  ),
  t('vinegar', 'H-ALC-04', 'review', [
    '식초',
    '비니거',
    '발사믹',
    'vinegar',
    'balsamic',
    '酢',
    '醋',
  ]),
  // ── 출처 증빙 (H-SRC). 같은 역할의 식물성 원료가 있으면 improve(보완할 것), 없으면 docs(서류) ──
  t(
    'gelatin',
    'H-SRC-01',
    'improve',
    [
      '젤라틴',
      '콜라겐',
      'gelatin',
      'gelatine',
      'collagen',
      'ゼラチン',
      'コラーゲン',
      '明胶',
      '胶原蛋白',
    ],
    { hintKey: 'gelatin' },
  ),
  t(
    'margarine',
    'H-SRC-02',
    'improve',
    [
      '마가린',
      '쇼트닝',
      '동물성 유지',
      '동물성유지',
      'margarine',
      'shortening',
      'animal fat',
      'マーガリン',
      'ショートニング',
      '人造黄油',
      '起酥油',
    ],
    { hintKey: 'margarine' },
  ),
  t(
    'emulsifier',
    'H-SRC-02',
    'improve',
    [
      '글리세린지방산에스테르',
      '모노·디글리세리드',
      '모노디글리세리드',
      '모노글리세리드',
      '디글리세리드',
      '초산 모노글리세리드',
      '젖산 모노글리세리드',
      '구연산 모노글리세리드',
      '주석산 모노글리세리드',
      '글리세린디아세틸주석산지방산에스테르',
      'DATEM',
      '자당지방산에스테르',
      '폴리글리세린지방산에스테르',
      '폴리글리세린축합리시놀레인산에스테르',
      'PGPR',
      '프로필렌글리콜지방산에스테르',
      '스테아릴젖산나트륨',
      '스테아릴젖산칼슘',
      '솔비탄지방산에스테르',
      '폴리소르베이트',
      'mono- and diglycerides',
      'monoglycerides',
      'diglycerides',
      'glycerol esters of fatty acids',
      'sucrose esters',
      'polyglycerol esters',
      'polyglycerol polyricinoleate',
      'propylene glycol esters',
      'sodium stearoyl lactylate',
      'calcium stearoyl lactylate',
      'sorbitan',
      'polysorbate',
      'E471',
      'E472',
      'E473',
      'E475',
      'E476',
      'E477',
      'E481',
      'E482',
      'グリセリン脂肪酸エステル',
      '乳化剤',
      '单甘酯',
      '甘油脂肪酸酯',
      '乳化剂',
    ],
    { hintKey: 'emulsifier' },
  ),
  t('fat-derived', 'H-SRC-02', 'docs', [
    '글리세린',
    '지방산염',
    '스테아르산',
    '스테아린산',
    '스테아르산마그네슘',
    '스테아린산마그네슘',
    '스테아린산칼슘',
    '트리아세틴',
    '대두레시틴',
    '레시틴',
    'glycerin',
    'glycerol',
    'stearic acid',
    'magnesium stearate',
    'calcium stearate',
    'stearate',
    'triacetin',
    'lecithin',
    'グリセリン',
    'レシチン',
    '甘油',
    '卵磷脂',
  ]),
  t(
    'rennet-dairy',
    'H-SRC-03',
    'docs',
    [
      '레닛',
      '렌넷',
      '우유응고효소',
      '펩신',
      '라이페이스',
      '리파아제',
      '치즈',
      '유청',
      '유청단백',
      '카제인',
      '카제인나트륨',
      'rennet',
      'pepsin',
      'lipase',
      'cheese',
      'whey',
      'casein',
      'caseinate',
      'レンネット',
      'チーズ',
      'ホエイ',
      '凝乳酶',
      '奶酪',
      '芝士',
      '乳清',
    ],
    { hintKey: 'rennet', except: ['미생물 레닛', '미생물레닛', 'microbial rennet'] },
  ),
  // 카민·코치닐·셸락·L-시스테인·곤충 유래는 학파·기관마다 판단이 달라 팩이 규칙으로 정하지 않았다 (halal.md 5-7).
  // 서류 규칙으로 단정하지 않고 '전문가 확인' 으로만 둔다 (화면 설명은 CERT_VARIANT_COPY 'H-SRC-04:unsettled')
  t(
    'unsettled-additive',
    'H-SRC-04',
    'review',
    [
      '카민',
      '코치닐',
      '쉘락',
      '셸락',
      'L-시스테인',
      '곤충',
      'carmine',
      'cochineal',
      'shellac',
      'cysteine',
      'insect',
      '胭脂虫',
      'カルミン',
      'コチニール',
      'シェラック',
      '虫胶',
    ],
    { variant: 'unsettled' },
  ),
  // 향료는 출처·용매(알코올) 확인과 할랄 인증 향료 (H-SRC-04). 카라멜색소 같은 첨가물도 같은 '첨가물 출처 증명서' 묶음이다
  t('flavour', 'H-SRC-04', 'docs', [
    '향료',
    '천연향료',
    '합성향료',
    '바닐라 추출물',
    '바닐라추출물',
    '바닐라 익스트랙',
    '카라멜색소',
    '캐러멜색소',
    'flavour',
    'flavor',
    'flavouring',
    'flavoring',
    'vanilla extract',
    'caramel color',
    'caramel colour',
    '香料',
    'フレーバー',
    'カラメル色素',
    '香精',
    '焦糖色',
  ]),
  t(
    'bone-char',
    'H-SRC-05',
    'docs',
    [
      '설탕',
      '백설탕',
      '흑설탕',
      '갈색설탕',
      '정백당',
      '골분인산',
      '식물탄',
      '활성탄',
      'sugar',
      'cane sugar',
      'bone char',
      'bone phosphate',
      'vegetable carbon',
      '砂糖',
      '白糖',
      '蔗糖',
    ],
    // 당알코올(에리스리톨 등)은 설탕이 아니라 감미료다
    { except: ['당알코올', 'sugar alcohol', '糖アルコール', '糖醇'] },
  ),
  t('microbial', 'H-SRC-06', 'docs', [
    '글루탐산나트륨',
    'L-글루탐산나트륨',
    'MSG',
    '이노신산나트륨',
    '구아닐산나트륨',
    '리보뉴클레오티드나트륨',
    '핵산',
    '효모추출물',
    '효모엑기스',
    '효모 추출물',
    '맥주효모',
    '잔탄검',
    '구연산',
    '미생물 레닛',
    '미생물레닛',
    '리보플라빈',
    '비타민 제제',
    'monosodium glutamate',
    'glutamate',
    'inosinate',
    'guanylate',
    'ribonucleotide',
    'yeast extract',
    "brewer's yeast",
    'xanthan',
    'citric acid',
    'microbial rennet',
    'riboflavin',
    'グルタミン酸ナトリウム',
    '酵母エキス',
    'キサンタン',
    'クエン酸',
    '味精',
    '谷氨酸钠',
    '酵母提取物',
    '黄原胶',
    '柠檬酸',
  ]),
  // ── 육류·가금 (H-ING-06, 할랄 도축 증빙). 복합원료보다 먼저 본다 (치킨스톡은 스톡이 아니라 고기) ──
  t(
    'meat',
    'H-ING-06',
    'docs',
    [
      '쇠고기',
      '소고기',
      '한우',
      '우육',
      '닭고기',
      '닭가슴살',
      '닭다리',
      '닭',
      '닭뼈',
      '닭발',
      '닭햄',
      '치킨',
      '치킨햄',
      '비프',
      '오리',
      '오리고기',
      '양고기',
      '염소고기',
      '칠면조',
      '사골',
      '사골육수',
      '사골국물',
      '우지',
      '소기름',
      '쇠고기 추출물',
      '닭고기 추출물',
      '고기 육수',
      '닭육수',
      '소고기 육수',
      '치킨스톡',
      '비프스톡',
      '소시지',
      '미트볼',
      '갈비',
      '등심',
      '안심',
      '차돌',
      '양지',
      '사태',
      '우족',
      '곱창',
      '대창',
      '막창',
      '육포',
      'beef',
      'chicken',
      'mutton',
      'lamb',
      'goat',
      'duck',
      'turkey',
      'veal',
      'tallow',
      'bone broth',
      'sausage',
      'meatball',
      '牛肉',
      '鶏肉',
      'チキン',
      '羊肉',
      '鸡肉',
      '鸭肉',
      '牛油',
    ],
    { hintKey: 'meat-stock' },
  ),
  t('composite', 'H-SRC-07', 'docs', [
    '간장',
    '양조간장',
    '진간장',
    '국간장',
    '고추장',
    '된장',
    '춘장',
    '쌈장',
    '소스',
    '시즈닝',
    '혼합제제',
    '조미료',
    '케첩',
    '마요네즈',
    '드레싱',
    '스톡',
    '부이용',
    '액젓',
    'soy sauce',
    'gochujang',
    'doenjang',
    'sauce',
    'seasoning',
    'ketchup',
    'mayonnaise',
    'dressing',
    'bouillon',
    'fish sauce',
    '醤油',
    'しょうゆ',
    'コチュジャン',
    '味噌',
    'ソース',
    '調味料',
    '酱油',
    '酱',
    '调味料',
  ]),
]

/** 해썹 원료 사전 (식품공전 장류·간장·소스 기준). 보존료는 종류별로 나눠 한도를 따로 본다 */
export const PRESERVATIVE_TERMS = {
  sorbate: [
    '소브산',
    '소브산칼륨',
    '소브산칼슘',
    '솔빈산',
    '솔빈산칼륨',
    'sorbic acid',
    'sorbate',
    'E200',
    'E202',
    'E203',
    'ソルビン酸',
    '山梨酸',
  ],
  benzoate: [
    '안식향산',
    '안식향산나트륨',
    '안식향산칼륨',
    '안식향산칼슘',
    '벤조산',
    '벤조산나트륨',
    'benzoic acid',
    'benzoate',
    'E210',
    'E211',
    'E212',
    'E213',
    '安息香酸',
    '苯甲酸',
  ],
  paraben: [
    '파라옥시안식향산',
    '파라옥시안식향산메틸',
    '파라옥시안식향산에틸',
    '파라벤',
    'paraben',
    'hydroxybenzoate',
    'パラベン',
    '尼泊金',
    '羟苯',
  ],
  other: [
    '프로피온산',
    '프로피온산나트륨',
    '프로피온산칼슘',
    '데히드로초산',
    '데히드로초산나트륨',
    '아질산나트륨',
    '나이신',
    '나타마이신',
    '폴리리신',
    '아황산나트륨',
    '메타중아황산',
    '보존료',
    'propionate',
    'propionic acid',
    'dehydroacetate',
    'sodium nitrite',
    'nisin',
    'natamycin',
    'polylysine',
    'sulfite',
    'metabisulfite',
    'preservative',
    '保存料',
    '防腐剂',
    '丙酸',
  ],
} as const
export type PreservativeKind = keyof typeof PRESERVATIVE_TERMS

/** '된장(무보존료)'·'preservative-free gochujang' 처럼 보존료가 없다는 말. 이런 이름은 보존료 원료로 보지 않는다 */
export const PRESERVATIVE_FREE =
  /무보존료|보존료\s?무첨가|보존료\s?없|preservative[- ]free|no preservatives?|without preservatives?|保存料無添加|無添加|不含防腐剂|无防腐剂/iu

/** 소브산염·안식향산염을 산으로 환산하는 비 (분자량 비). 기준은 '소브산으로서', '안식향산으로서' 이다 */
export const PRESERVATIVE_ACID_FACTOR: Record<string, number> = {
  소브산칼륨: 0.746,
  솔빈산칼륨: 0.746,
  'potassium sorbate': 0.746,
  소브산칼슘: 0.855,
  'calcium sorbate': 0.855,
  안식향산나트륨: 0.847,
  벤조산나트륨: 0.847,
  'sodium benzoate': 0.847,
  안식향산칼륨: 0.764,
  'potassium benzoate': 0.764,
}

/** 타르색소 (식용색소 적색 제40호 같은 이름, FD&C Red No. 40, E 번호) */
export const TAR_COLOR_WORDS = [
  '타르색소',
  'FD&C',
  'allura red',
  'tartrazine',
  'sunset yellow',
  'brilliant blue',
  'indigo carmine',
  'erythrosine',
  'E102',
  'E110',
  'E124',
  'E127',
  'E129',
  'E132',
  'E133',
  'タール色素',
  '合成着色料',
  '合成色素',
  '胭脂红',
  '柠檬黄',
  '日落黄',
  '诱惑红',
  '亮蓝',
]
/** '식용색소 적색 제40호', '적색40호', 'Red No. 40', 'Yellow 5', '赤色102号', '赤色2号' */
export const TAR_COLOR_PATTERN =
  /(?:적색|황색|청색|녹색|赤色|黄色|青色|緑色)\s*제?\s*\d+\s*호|\b(?:red|yellow|blue|green)\s*(?:no\.?\s*)?\d+\b|[赤黄青緑]色\s*\d+\s*号/iu

export const RED_YEAST_WORDS = [
  '홍국',
  '홍국색소',
  '모나스커스',
  'monascus',
  'red yeast rice',
  '紅麹',
  '红曲',
]

export const RED_PEPPER_POWDER_WORDS = [
  '고춧가루',
  '고추가루',
  '고추분말',
  '고추 분말',
  'red pepper powder',
  'red pepper flakes',
  'chili powder',
  'chilli powder',
  'gochugaru',
  '唐辛子粉',
  '一味唐辛子',
  '辣椒粉',
  '辣椒面',
]

/** 혼합장의 '장류 50% 이상' 을 셀 때 장으로 보는 재료 */
export const JANG_INGREDIENT_WORDS = [
  '된장',
  '고추장',
  '간장',
  '춘장',
  '청국장',
  'doenjang',
  'gochujang',
  'soy sauce',
  'soybean paste',
  'chili paste',
  '味噌',
  'コチュジャン',
  '醤油',
  '大酱',
  '辣椒酱',
  '酱油',
]

// ── 원료 단위로 나누고 찾기 ─────────────────────────────────────────

/** 원료 단위 하나 (inParens = 괄호 속) */
export interface IngredientUnit {
  text: string
  inParens: boolean
}

/** 괄호 속이 원산지 표기면 (국산, 미국산, origin …) 그 안은 원료로 보지 않는다 */
const ORIGIN_IN_PARENS = /국산|외국산|수입산|[가-힣]+산$|원산지|origin|product of|産|产/iu

/**
 * 괄호에 수식어만 있으면 ('맛술(무알콜)', '된장(무보존료)') 원료가 아니라 앞 원료를 꾸미는 말이다.
 * 앞 원료에 붙여 읽는다 ('무알콜 맛술') — 수식어를 보는 규칙(requires·무보존료)이 같은 원료 단위에서 찾게
 */
const QUALIFIER_IN_PARENS =
  /([^,，、·・/;；(（[［]+?)\s*[(（[［]\s*(무알코올|무알콜|논알코올|논알콜|non-alcoholic|alcohol-free|ノンアルコール|无醇|무보존료|보존료\s?무첨가|preservative-free|保存料無添加|不含防腐剂)\s*[)）\]］]/giu

/** 재료 이름 → 원료 단위들. "데리야키소스(간장, 미림)" → 데리야키소스 · 간장 · 미림 */
export function splitIngredientUnits(name: string): IngredientUnit[] {
  const text = name.normalize('NFKC').replace(QUALIFIER_IN_PARENS, '$2 $1')
  const units: IngredientUnit[] = []
  const outside = text.replace(/[(（[［]([^)）\]］]*)[)）\]］]/gu, (_m, inner: string) => {
    for (const part of inner.split(/[,，、·・/;；]/u)) {
      const p = part.trim()
      if (p && !ORIGIN_IN_PARENS.test(p)) units.push({ text: p, inParens: true })
    }
    return ' '
  })
  for (const part of outside.split(/[,，、·・/;；]/u)) {
    const p = part.trim()
    if (p) units.push({ text: p, inParens: false })
  }
  return units
}

const LATIN = /[a-z]/i
/** 영어 말인지 (라틴 글자가 있고 ASCII 밖 글자가 없다) — 영어는 단어 경계로 찾는다 */
const isEnglishWord = (w: string) => LATIN.test(w) && [...w].every((c) => c.charCodeAt(0) < 128)
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** 두 글자 이하 말 뒤에 붙어도 같은 원료로 보는 가공 꼬리 ('치즈분말', '혈장단백질', '닭뼈', '사골농축액', '치킨파우더') */
const SHORT_WORD_SUFFIX =
  /^(?:분말|가루|파우더|농축액|엑기스|추출물|단백질?|육수|뼈|발|껍질|기름|유|베이스|오일|시즈닝|스톡)$/u

/** 원료 단위 하나에 말이 있는지 (위 머리 설명의 규칙) */
export function unitHasWord(unit: string, word: string, contains = false): boolean {
  const lowered = unit.normalize('NFKC').toLowerCase()
  const w = word.normalize('NFKC').toLowerCase()
  if (isEnglishWord(w)) {
    return new RegExp(`(?:^|[^a-z0-9])${escapeRe(w)}(?:s|es)?(?![a-z0-9])`, 'iu').test(lowered)
  }
  const squeezed = lowered.replace(/\s+/g, '')
  const target = w.replace(/\s+/g, '')
  if (contains || [...target].length >= 3) return squeezed.includes(target)
  if (squeezed === target || squeezed.endsWith(target)) return true
  // 짧은 말 + 가공 꼬리로 끝나는지 ('가공치즈분말' 의 치즈 + 분말)
  for (let at = squeezed.indexOf(target); at >= 0; at = squeezed.indexOf(target, at + 1)) {
    if (SHORT_WORD_SUFFIX.test(squeezed.slice(at + target.length))) return true
  }
  return false
}

/** 원료 단위에서 먼저 걸리는 사전 용어. 없으면 null */
export function matchTerm(
  unit: IngredientUnit,
  terms: readonly IngredientTerm[],
): { term: IngredientTerm; word: string } | null {
  for (const term of terms) {
    if (term.notInParens && unit.inParens) continue
    if (term.except?.some((e) => unitHasWord(unit.text, e, true))) continue
    if (term.requires && !term.requires.some((r) => unitHasWord(unit.text, r, true))) continue
    const word = term.words.find((w) => unitHasWord(unit.text, w, term.contains))
    if (word) return { term, word }
  }
  return null
}

/** 문장 끝 조사 (공정 글의 '맛술을'·'라드로' 에서 원료 이름만 남긴다) */
const KO_PARTICLE = /(?:으로|에서|까지|부터|이나|과|와|을|를|이|가|은|는|로|에|도|만|의)$/u
/** 빼거나 바꾼다는 문장 ('맛술 대신 배즙', '돼지 유래가 닿은 설비는 세척') 은 원료를 쓴다는 뜻이 아니다 */
const NOT_USED =
  /대신|없이|빼고|빼서|제외|금지|않|말 ?것|닿은|닿지|세척|분리|비할랄|instead|without|free (?:of|from)|(?:alcohol|pork)-free|avoid|\bnot\b|\bno\b|never|separat|clean|代わり|使わ|除|不使用|不含|代替|分开|清洗/iu

/**
 * 공정 글(단계 제목·설명·설비)에서 사전의 '금지' 용어를 찾는다. 재료 칸에 없이 공정에서만 쓰는 술·돼지 원료
 * ('맛술 2큰술을 넣고 볶는다', '라드로 팬을 코팅') 를 잡는다. 빼거나 바꾼다는 문장은 건너뛴다.
 * 자유 글이라 오탐이 있을 수 있어 재료 칸처럼 '불합격' 으로 막지 않고 전문가 확인으로 쓴다 (checks.ts)
 */
export function textTermHits(
  text: string,
  terms: readonly IngredientTerm[],
): { term: IngredientTerm; word: string }[] {
  const hits: { term: IngredientTerm; word: string }[] = []
  const add = (hit: { term: IngredientTerm; word: string } | null) => {
    if (hit && !hits.some((h) => h.term === hit.term)) hits.push(hit)
  }
  for (const sentence of text.normalize('NFKC').split(/[.。!?！？\n;；]/u)) {
    if (!sentence.trim() || NOT_USED.test(sentence)) continue
    // 1) 문장 전체: 영어(단어 경계)·한글 세 글자 이상·한자·가나 두 글자 이상 ('料酒' — 띄어 쓰지 않는 글)
    for (const term of terms) {
      if (term.except?.some((e) => unitHasWord(sentence, e, true))) continue
      if (term.requires && !term.requires.some((r) => unitHasWord(sentence, r, true))) continue
      const word = term.words.find((w) => {
        const len = [...w.replace(/\s+/g, '')].length
        if (isEnglishWord(w) || len >= 3 || (len === 2 && !/[가-힣]/u.test(w)))
          return unitHasWord(sentence, w, true)
        return false
      })
      if (word) add({ term, word })
    }
    // 2) 낱말: 한글 두 글자 이하 말은 조사를 뗀 낱말과 같거나 끝이 같을 때 ('맛술을' → 맛술). 한 글자 말은 낱말과 같을 때만 ('기술' 은 술이 아님)
    for (const token of sentence.split(/[\s,，、·・/()（）[\]［］]+/u)) {
      const bare = token.replace(KO_PARTICLE, '')
      if (!bare) continue
      const hit = matchTerm({ text: bare, inParens: false }, terms)
      if (hit && ([...hit.word].length > 1 || bare === hit.word)) add(hit)
    }
  }
  return hits
}

/** 이름(원료 단위 전부)에 목록의 말이 하나라도 있으면 그 말 */
export function findWord(name: string, words: readonly string[]): string | null {
  for (const unit of splitIngredientUnits(name)) {
    const word = words.find((w) => unitHasWord(unit.text, w))
    if (word) return word
  }
  return null
}

/** 자유 글(공정·메모)에 목록의 말이 있는지. 단어 경계가 아니라 들어 있으면 찾는다 (영어는 단어 경계) */
export function textHasAny(text: string, words: readonly string[]): boolean {
  const lowered = text.normalize('NFKC').toLowerCase()
  return words.some((w) => {
    const lw = w.toLowerCase()
    return isEnglishWord(lw)
      ? new RegExp(`(?:^|[^a-z0-9])${escapeRe(lw)}`, 'iu').test(lowered)
      : lowered.includes(lw)
  })
}
